import React, { useCallback } from "react";
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	CircularProgress,
	TablePagination,
} from "@mui/material";
import { EstoqueService } from "../../service/estoque_service";
import useEstoqueLista from "./useEstoqueLista";
import { formatDate, formatNumber } from "./formatters";

/**
 * Estoque de saídas: produto final gerado pelas ordens de produção, com o
 * quanto já foi vendido e o que sobrou.
 */
function EstoqueSaidas({ unidadeDistribuidoraId, searchTerm }) {
	const fetcher = useCallback(
		(unidadeId, search) => new EstoqueService().getEstoque(unidadeId, search),
		[]
	);

	const {
		itens,
		itensPaginados,
		loading,
		page,
		rowsPerPage,
		handleChangePage,
		handleChangeRowsPerPage,
	} = useEstoqueLista(fetcher, unidadeDistribuidoraId, searchTerm);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<CircularProgress sx={{ color: "#1a9e8f" }} />
			</Box>
		);
	}

	if (itens.length === 0) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "400px",
					gap: 2,
					p: 4,
				}}
			>
				<Typography variant="h6" sx={{ color: "#999", textAlign: "center" }}>
					Nenhum produto em estoque
				</Typography>
				<Typography variant="body2" sx={{ color: "#999", textAlign: "center" }}>
					{searchTerm
						? "Tente fazer uma busca diferente"
						: "Não há produtos finalizados em estoque"}
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow sx={{ bgcolor: "#f5f5f5" }}>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>
								Data de Entrada
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>
								Produto Final
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }}>
								Código de Rastreio
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }} align="right">
								Qtd. de Entrada
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }} align="right">
								Quantidade Total
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }} align="right">
								Quantidade Vendida
							</TableCell>
							{/* O que outras OPs consumiram deste produto tambem sai do disponivel */}
							<TableCell sx={{ fontWeight: 600, color: "#666" }} align="right">
								Utilizada em OP
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: "#666" }} align="right">
								Total Disponível
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{itensPaginados.map((item) => {
							const cultura = item.produtoFinal?.cultura || "-";
							const variedade = item.produtoFinal?.variedade
								? ` - ${item.produtoFinal.variedade}`
								: "";
							const produtoDescricao = `${cultura}${variedade}`;

							return (
								<TableRow
									key={item.id}
									sx={{
										"&:hover": {
											bgcolor: "#f9f9f9",
										},
									}}
								>
									<TableCell>
										<Typography variant="body2">{formatDate(item.data)}</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2" sx={{ fontWeight: 500 }}>
											{produtoDescricao}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2">
											{item.loteRastreabilidade || "-"}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">
											{formatNumber(item.quantidadeEntrada)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">
											{formatNumber(item.quantidadeTotal)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">
											{formatNumber(item.quantidadeVendida)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">
											{formatNumber(item.quantidadeConsumida)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography
											variant="body2"
											sx={{
												fontWeight: 600,
												color:
													item.quantidadeDisponivel <= 0 ? "#ff4d4d" : "#1a9e8f",
											}}
										>
											{formatNumber(item.quantidadeDisponivel)}
										</Typography>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				count={itens.length}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage="Linhas por página:"
				labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
				sx={{
					borderTop: "1px solid #e0e0e0",
					".MuiTablePagination-toolbar": {
						color: "#666",
					},
				}}
			/>
		</>
	);
}

export default EstoqueSaidas;
